import React, { useState, useEffect, useRef } from 'react';
import { useCrmContacts } from '@/hooks/queries';

export default function ContactSearchSelect({ value, onChange, selectedContactName = null }) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [displayValue, setDisplayValue] = useState(selectedContactName || '');
  const wrapperRef = useRef(null);

  const { data, isLoading } = useCrmContacts(1, 20, null, search || null);
  const contacts = data?.contacts || [];

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update display when selectedContactName changes
  useEffect(() => {
    if (selectedContactName) {
      setDisplayValue(selectedContactName);
    }
  }, [selectedContactName]);

  const getContactName = (contact) => {
    const parts = [contact.firstName, contact.lastName].filter(Boolean);
    return parts.join(' ') || 'Unknown';
  };

  const handleSelect = (contact) => {
    onChange(contact.id);
    setDisplayValue(getContactName(contact));
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = () => {
    onChange(null);
    setDisplayValue('');
    setSearch('');
  };

  return (
    <div ref={wrapperRef} className="relative">
      {value && displayValue ? (
        <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50">
          <span className="flex-1">{displayValue}</span>
          <button
            type="button"
            onClick={handleClear}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ) : (
        <div>
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder="Search contacts..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
          />

          {isOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {isLoading ? (
                <div className="px-3 py-2 text-sm text-gray-500">Searching...</div>
              ) : contacts.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">
                  {search ? 'No contacts found' : 'Type to search contacts'}
                </div>
              ) : (
                contacts.map((contact) => (
                  <button
                    key={contact.id}
                    type="button"
                    onClick={() => handleSelect(contact)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
                  >
                    <div className="w-6 h-6 bg-zapt-purple/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-zapt-purple text-xs font-medium">
                        {contact.firstName?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 text-sm">
                        {getContactName(contact)}
                      </div>
                      {contact.company && (
                        <div className="text-xs text-gray-500">{contact.company}</div>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
